import { PARTS_FISCALES } from '@shared/constants/BaremeIRPP';
import type { Salarie } from '@shared/types/Salarie';

/** Durée pendant laquelle le veuf est traité comme un marié (CGI art. 170 : « 2 ans suivant le décès »). */
const ANNEES_VEUVAGE_ASSIMILE_MARIE = 2;

/** Vrai si `dateRef` tombe dans les deux ans qui suivent le décès (`dateDeces` incluse, +2 ans exclus). */
function dansLesDeuxAnsDuDeces(dateDeces: string, dateRef: string): boolean {
  const deces = dateDeces.slice(0, 10);
  const ref = dateRef.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(deces) || !/^\d{4}-\d{2}-\d{2}$/.test(ref)) return false;
  const fin = `${Number(deces.slice(0, 4)) + ANNEES_VEUVAGE_ASSIMILE_MARIE}${deces.slice(4)}`;
  return ref >= deces && ref < fin;
}

/**
 * Parts fiscales (CGI art. 168-170 ; page DGI « Assiette et liquidation de l'IRPP », §3.a).
 *  - célibataire, divorcé ou veuf sans enfant : 1 ; marié sans enfant : 2 ;
 *  - célibataire ou divorcé : 1 enfant = 2, 2 = 2,5, 3 = 3, 4 = 3,5 (soit 1 + 0,5 parent isolé + 0,5 par enfant) ;
 *  - marié : 2 + 0,5 par enfant ; puis +0,5 par enfant dans la limite de six enfants ;
 *  - veuf pendant les deux ans qui suivent le décès du conjoint : traité comme un marié (art. 170, dernier alinéa) ;
 *    nécessite `date_deces_conjoint` et une date de référence (date du bulletin) ; sans elles : traité comme un veuf ordinaire.
 *  - marié dont le conjoint est actif : 1 part. Règle PRUDENTE de l'ERP, sans texte dans les pages DGI : l'art. 170 ne
 *    traite en célibataire que les époux en imposition distincte (art. 77 : séparation de biens avec vie séparée,
 *    instance de séparation ou de divorce, abandon du domicile), pas tout ménage à deux salaires. À faire valider.
 * Non gérés (données absentes) : enfant infirme (+1 au lieu de +0,5, art. 171) et 1,5 part de l'art. 171.
 *
 * @param dateRef date de référence AAAA-MM-JJ (ou AAAA-MM) pour l'appréciation du veuvage ; sans elle, la date du jour.
 */
export function calculerParts(
  salarie: Pick<Salarie, 'situation' | 'enfants' | 'nb_enfants' | 'conjoint_actif'> &
    Partial<Pick<Salarie, 'date_deces_conjoint'>>,
  dateRef?: string,
): number {
  const enfants = salarie.enfants ?? salarie.nb_enfants ?? 0;
  const ref = dateRef
    ? /^\d{4}-\d{2}$/.test(dateRef)
      ? `${dateRef}-01`
      : dateRef
    : new Date().toISOString().slice(0, 10);
  const veufAssimileMarie =
    salarie.situation === 'veuf' &&
    salarie.date_deces_conjoint !== undefined &&
    salarie.date_deces_conjoint !== '' &&
    dansLesDeuxAnsDuDeces(salarie.date_deces_conjoint, ref);

  const compteCommeMarie = salarie.situation === 'marie' || veufAssimileMarie;
  let parts = compteCommeMarie ? PARTS_FISCALES.MARIE : PARTS_FISCALES.CELIBATAIRE;
  const conjointActif = salarie.conjoint_actif ?? false;

  if (salarie.situation === 'marie' && conjointActif) {
    parts = PARTS_FISCALES.CELIBATAIRE;
  }

  const enfantsComptes = Math.min(enfants, PARTS_FISCALES.ENFANTS_MAX_COMPTES);
  parts += enfantsComptes * PARTS_FISCALES.ENFANT;

  if (!compteCommeMarie && enfants > 0) {
    parts += PARTS_FISCALES.PARENT_ISOLE_ENFANT;
  }

  return Math.min(parts, PARTS_FISCALES.MAX_PARTS);
}

export type StatutVeuvage = 'non_concerne' | 'sans_date' | 'dans_delai' | 'delai_depasse' | 'date_future';

/** Situation du veuvage à la date de référence (AAAA-MM-JJ) : sert à expliquer le nombre de parts à l'utilisateur. */
export function statutVeuvage(
  situation: Salarie['situation'] | undefined,
  dateDecesConjoint: string | undefined,
  dateRef: string,
): StatutVeuvage {
  if (situation !== 'veuf') return 'non_concerne';
  const deces = (dateDecesConjoint ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(deces)) return 'sans_date';
  if (deces > dateRef.slice(0, 10)) return 'date_future';
  return dansLesDeuxAnsDuDeces(deces, dateRef) ? 'dans_delai' : 'delai_depasse';
}

/** Texte d'aide affiché sous la date du décès du conjoint (français simple). */
export function messageVeuvage(statut: StatutVeuvage): string {
  switch (statut) {
    case 'sans_date':
      return 'Sans cette date, le salarié est traité comme un veuf ordinaire (1 part, plus les enfants).';
    case 'dans_delai':
      return 'Le décès date de moins de 2 ans : le salarié garde les parts d’un marié (2 parts, plus les enfants).';
    case 'delai_depasse':
      return 'Le décès date de plus de 2 ans : le salarié est traité comme un veuf ordinaire (1 part, plus les enfants).';
    case 'date_future':
      return 'Cette date est postérieure à aujourd’hui : vérifiez la saisie.';
    default:
      return '';
  }
}
